import { useState, useEffect, useRef, useCallback } from 'react';
import EcoSpace from 'eco-design/base/eco-space';
import EcoSpaceSmall from 'eco-design/base/eco-space-small';
import EcoSectionCommon from 'eco-design/elements/eco-section-common';
import { useMediaDevice } from '@elem/compPage/Oasis/hooks';
import EfImg from '@elem/ui/ef-img';
import { Pagination, Swiper as SwiperProps } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Styled from './index.module.scss';
import { useLang } from '@/i18n';

const DURATION = 6000; // 动画持续时间（毫秒）

const SectionU1 = () => {
    const { t } = useLang('theProduct.SectionU1');
    const { isH5 } = useMediaDevice();

    const [swiper, setSwiper] = useState<SwiperProps | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const animationIdRef = useRef<number | null>(null);
    const initAniTimeRef = useRef<number>(0);
    const aniFrameTimeRef = useRef<number>(0);

    const imgLists: Record<string, string>[] = [
        {
            pc: 'https://websiteoss.ecoflow.com/upload/image/1745408063873/p1_h5.jpg',
            h5: 'https://websiteoss.ecoflow.com/upload/image/1745408063873/p1_h5.jpg',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/upload/image/1745408080070/p2.jpg',
            h5: 'https://websiteoss.ecoflow.com/upload/image/1745408083067/p2_h5.jpg',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/upload/image/1745408087545/p3.jpg',
            h5: 'https://websiteoss.ecoflow.com/upload/image/1745408090332/p3_h5.jpg',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/upload/image/1745408093789/p4.jpg',
            h5: 'https://websiteoss.ecoflow.com/upload/image/1745408096026/p4_h5.jpg',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/upload/image/1745408098842/p5.jpg',
            h5: 'https://websiteoss.ecoflow.com/upload/image/1745408101416/p5_h5.jpg',
        },
    ];

    const swiperConfig = {
        modules: [Pagination],
        slidesPerView: 'auto' as const,
        initialSlide: 0,
        loop: true,
        loopAdditionalSlides: 3, // 增加额外的幻灯片数量
        loopPreventsSlide: false, // 防止循环模式下的滑动问题
        watchSlidesProgress: true,
        observer: true, // 监视DOM变化
        observeParents: true, // 监视父元素变化
        spaceBetween: 16,
        pagination: {
            clickable: true,
        },
        onSwiper: (swipwerIns: SwiperProps) => {
            setTimeout(() => {
                setSwiper(swipwerIns);
            }, 300);
        },
        onSlideChange: (swiper: SwiperProps) => {
            if (!swiper || typeof swiper.realIndex === 'undefined') return;
            setActiveIndex(swiper.realIndex);
            initAniTimeRef.current = 0;
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
                animationIdRef.current = null;
            }

            doAnimate();
        },
    };

    // 执行动画的函数
    const animate = useCallback(() => {
        if (!initAniTimeRef.current) {
            initAniTimeRef.current = new Date().getTime();
        }
        aniFrameTimeRef.current = new Date().getTime();

        const container = document.querySelector('.story .btns');
        const element = container?.querySelector('.circle circle');

        // 计算动画进度（0 到 1 之间的值）
        if (element && swiper && container) {
            const judgeTime = aniFrameTimeRef.current - initAniTimeRef.current;
            const progress = Math.min(judgeTime / DURATION, 1);
            (element as SVGCircleElement).style.strokeDashoffset = `${100 - progress * 100}px`;
            if (judgeTime >= DURATION) {
                swiper.slideNext();
            }
            animationIdRef.current = requestAnimationFrame(animate);
        }
    }, [swiper]);

    const handlePrev = () => {
        swiper?.slidePrev();
    };

    const handleNext = () => {
        swiper?.slideNext();
    };

    const doAnimate = useCallback(() => {
        if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = requestAnimationFrame(animate);
    }, [animate]);

    useEffect(() => {
        if (typeof window !== 'undefined' && swiper && isH5) {
            doAnimate();
        }
    }, [swiper, doAnimate, isH5]);

    useEffect(() => {
        return () => {
            // 组件卸载时清理
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
                animationIdRef.current = null;
            }
            if (swiper) {
                swiper.destroy(true, true);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={Styled.SectionU1}>
            <EcoSpace />
            <EcoSectionCommon title={t.rich('title', { sup: (text: string) => <sup>{text}</sup> })} desc={t('desc')} />
            <EcoSpaceSmall />
            <div className="eco-content">
                {!isH5 ? (
                    <div className="pc-container">
                        <div className="left">
                            <div className="p1">
                                <EfImg pc={imgLists[0].pc} />
                            </div>
                            <div className="p2">
                                <EfImg pc={imgLists[1].pc} />
                            </div>
                        </div>
                        <div className="center">
                            <div className="p3">
                                <EfImg pc={imgLists[2].pc} />
                            </div>
                        </div>
                        <div className="right">
                            <div className="p4">
                                <EfImg pc={imgLists[3].pc} />
                            </div>
                            <div className="p5">
                                <EfImg pc={imgLists[4].pc} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <Swiper className="story" {...swiperConfig}>
                        {imgLists.map((item, index) => (
                            <SwiperSlide className={`card item-${index + 1}`} key={index}>
                                <div className="item-img">
                                    <EfImg pc={item?.pc} pad={item?.h5} h5={item?.h5} />
                                </div>
                            </SwiperSlide>
                        ))}
                        <div className="btns">
                            <span className={`btn prev ${swiper?.activeIndex === 0 ? 'disabled' : ''}`} onClick={handlePrev}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54" fill="none">
                                    <g clipPath="url(#clip0_35_6276)">
                                        <circle cx="27" cy="27" r="27" transform="matrix(-1 0 0 1 54 0)" fill="#F4F4F4" />
                                        <path
                                            d="M29.25 33.75L22.5 27L29.25 20.25"
                                            stroke="black"
                                            strokeWidth="2.25"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_35_6276">
                                            <rect width="54" height="54" fill="white" transform="matrix(-1 0 0 1 54 0)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                            <span className="btn next" onClick={handleNext}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54" fill="none">
                                    <g clipPath="url(#clip0_35_6279)">
                                        <circle cx="27" cy="27" r="27" fill="#F4F4F4" />
                                        <path
                                            d="M24.75 33.75L31.5 27L24.75 20.25"
                                            stroke="black"
                                            strokeWidth="2.25"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_35_6279">
                                            <rect width="54" height="54" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <svg className="circle" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" preserveAspectRatio="none">
                                    <circle
                                        cx="18"
                                        cy="18"
                                        r="16"
                                        fill="transparent"
                                        stroke="#FAA10E"
                                        strokeWidth="2"
                                        strokeDashoffset="100"
                                        strokeLinecap="round"
                                        strokeDasharray="100 100"
                                    />
                                </svg>
                            </span>
                        </div>
                    </Swiper>
                )}
            </div>
        </div>
    );
};

export default SectionU1;
